// src/components/ModalAdministrarFinanciera.jsx

import React, {
  useEffect,
  useState,
} from "react";

import {
  Button,
  Modal,
  Spinner,
} from "react-bootstrap";

import {
  postFinancieraAdmin,
  putFinancieraAdmin,
  subirLogoFinanciera,
  eliminarLogoFinanciera,
} from "../helpers/rutaFinancieras";

const clean = (v) =>
  String(v ?? "").trim();

const getId = (f) =>
  f?._id || f?.id || "";

const estadoVacio = {
  codfinanciera: "",

  financiera: "",
  razonsocial: "",
  cuit: "",
  domicilio: "",

  telefono1: "",
  email: "",
  contacto: "",

  observaciones: "",

  activo: true,

  feePorcentaje: 0,

  tipoCalculo:
    "GROSS_UP",

  requiereCuotas: false,

  aplicaTax: true,

  configuracionPagosActiva: false,

  reglasCuotas: [],
};

const hidratar = (
  financiera
) => {
  if (!financiera) {
    return {
      ...estadoVacio,
    };
  }

  return {
    codfinanciera:
      financiera
        ?.codfinanciera || "",

    financiera:
      clean(
        financiera?.financiera ??
          financiera?.nombre
      ),

    razonsocial:
      clean(
        financiera
          ?.razonsocial
      ),

    cuit:
      clean(
        financiera?.cuit
      ),

    domicilio:
      clean(
        financiera
          ?.domicilio
      ),

    telefono1:
      clean(
        financiera
          ?.telefono1 ??
          financiera
            ?.telefono
      ),

    email:
      clean(
        financiera?.email
      ),

    contacto:
      clean(
        financiera
          ?.contacto
      ),

    observaciones:
      clean(
        financiera
          ?.observaciones
      ),

    activo:
      financiera?.activo !==
      false,

    feePorcentaje:
      Number(
        financiera
          ?.feePorcentaje || 0
      ),

    tipoCalculo:
      clean(
        financiera
          ?.tipoCalculo ||
          "GROSS_UP"
      ).toUpperCase(),

    requiereCuotas:
      Boolean(
        financiera
          ?.requiereCuotas
      ),

    aplicaTax:
      financiera?.aplicaTax !==
      false,

    configuracionPagosActiva:
      financiera
        ?.configuracionPagosActiva ===
      true,

    reglasCuotas:
      Array.isArray(
        financiera
          ?.reglasCuotas
      )
        ? financiera.reglasCuotas.map(
            (regla) => ({
              cuotas:
                regla?.cuotas ??
                "",

              feePorcentaje:
                regla
                  ?.feePorcentaje ??
                0,
            })
          )
        : [],
  };
};

const obtenerFinancieraRespuesta =
  (resp) => {
    if (!resp) {
      return null;
    }

    if (
      resp?._id ||
      resp?.id
    ) {
      return resp;
    }

    if (
      resp?.financiera &&
      typeof resp.financiera ===
        "object"
    ) {
      return resp.financiera;
    }

    if (
      resp?.data
        ?.financiera
    ) {
      return resp.data
        .financiera;
    }

    return null;
  };

const ModalAdministrarFinanciera =
  ({
    show,
    onHide,
    financiera,
    onSaved,
  }) => {
    const [
      form,
      setForm,
    ] = useState(
      estadoVacio
    );

    const [
      saving,
      setSaving,
    ] = useState(false);

    const [
      error,
      setError,
    ] = useState("");

    const [
      archivoLogo,
      setArchivoLogo,
    ] = useState(null);

    const [
      previewLogo,
      setPreviewLogo,
    ] = useState("");

    const [
      eliminarLogoPendiente,
      setEliminarLogoPendiente,
    ] = useState(false);

    /*
      Si creamos una financiera
      y luego falla solamente el logo,
      conservamos el ID creado para
      no volver a crear otra.
    */
    const [
      idPersistido,
      setIdPersistido,
    ] = useState("");

    const esEdicion =
      Boolean(
        getId(financiera) ||
          idPersistido
      );

    /* ====================================================
       HIDRATAR
    ==================================================== */

    useEffect(() => {
      if (!show) {
        return;
      }

      setForm(
        hidratar(financiera)
      );

      setError("");

      setArchivoLogo(null);

      setEliminarLogoPendiente(
        false
      );

      setIdPersistido(
        getId(financiera)
      );

      setPreviewLogo(
        financiera?.logo
          ?.url || ""
      );
    }, [
      show,
      financiera,
    ]);

    /*
      Limpiar previews blob.
    */
    useEffect(() => {
      return () => {
        if (
          previewLogo &&
          previewLogo.startsWith(
            "blob:"
          )
        ) {
          URL.revokeObjectURL(
            previewLogo
          );
        }
      };
    }, [previewLogo]);

    /* ====================================================
       CAMPOS
    ==================================================== */

    const handleChange = (
      e
    ) => {
      const {
        name,
        value,
      } = e.target;

      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const cambiarActivo = () => {
      setForm((prev) => ({
        ...prev,
        activo:
          !prev.activo,
      }));
    };

    const cambiarAplicaTax = () => {
      setForm((prev) => ({
        ...prev,

        aplicaTax:
          !prev.aplicaTax,
      }));
    };

    const cambiarConfiguracionPagosActiva =
      () => {
        setForm((prev) => ({
          ...prev,

          configuracionPagosActiva:
            !prev.configuracionPagosActiva,
        }));
      };

    const cambiarRequiereCuotas =
      () => {
        setForm((prev) => {
          const nuevo =
            !prev.requiereCuotas;

          let tipoCalculo =
            prev.tipoCalculo;

          /*
            Si quitamos cuotas y estaba
            específicamente en POR_CUOTA,
            volvemos a GROSS_UP.

            FONDEO_INVERSO puede existir
            con o sin cuotas.
          */
          if (
            !nuevo &&
            tipoCalculo ===
              "POR_CUOTA"
          ) {
            tipoCalculo =
              "GROSS_UP";
          }

          return {
            ...prev,

            requiereCuotas:
              nuevo,

            tipoCalculo,

            reglasCuotas:
              nuevo
                ? prev.reglasCuotas
                : [],
          };
        });
      };

    const cambiarTipoCalculo =
      (e) => {
        const value =
          String(
            e.target.value ||
              ""
          ).toUpperCase();

        setForm((prev) => ({
          ...prev,

          tipoCalculo:
            value,

          /*
            POR_CUOTA obliga cuotas.

            FONDEO_INVERSO puede
            trabajar con o sin cuotas.
          */
          requiereCuotas:
            value ===
            "POR_CUOTA"
              ? true
              : prev.requiereCuotas,
        }));
      };

    /* ====================================================
       CUOTAS
    ==================================================== */

    const agregarCuota = () => {
      setForm((prev) => ({
        ...prev,

        requiereCuotas:
          true,

        /*
          No cambiamos tipoCalculo.

          Así FONDEO_INVERSO puede tener
          reglas distintas según cuotas.
        */
        reglasCuotas: [
          ...prev.reglasCuotas,

          {
            cuotas: "",
            feePorcentaje: 0,
          },
        ],
      }));
    };

    const cambiarRegla = (
      index,
      campo,
      valor
    ) => {
      setForm((prev) => ({
        ...prev,

        reglasCuotas:
          prev.reglasCuotas.map(
            (
              regla,
              idx
            ) =>
              idx === index
                ? {
                    ...regla,
                    [campo]:
                      valor,
                  }
                : regla
          ),
      }));
    };

    const eliminarRegla = (
      index
    ) => {
      setForm((prev) => ({
        ...prev,

        reglasCuotas:
          prev.reglasCuotas.filter(
            (_, idx) =>
              idx !== index
          ),
      }));
    };

    /* ====================================================
       LOGO
    ==================================================== */

    const seleccionarLogo =
      (e) => {
        const archivo =
          e.target.files?.[0];

        if (!archivo) {
          return;
        }

        const permitidos = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/webp",
        ];

        if (
          !permitidos.includes(
            archivo.type
          )
        ) {
          setError(
            "El logo debe ser JPG, PNG o WEBP."
          );

          return;
        }

        if (
          archivo.size >
          2 * 1024 * 1024
        ) {
          setError(
            "El logo no puede superar 2 MB."
          );

          return;
        }

        if (
          previewLogo &&
          previewLogo.startsWith(
            "blob:"
          )
        ) {
          URL.revokeObjectURL(
            previewLogo
          );
        }

        const preview =
          URL.createObjectURL(
            archivo
          );

        setArchivoLogo(
          archivo
        );

        setPreviewLogo(
          preview
        );

        setEliminarLogoPendiente(
          false
        );

        setError("");
      };

    const marcarEliminarLogo =
      () => {
        if (
          previewLogo &&
          previewLogo.startsWith(
            "blob:"
          )
        ) {
          URL.revokeObjectURL(
            previewLogo
          );
        }

        setPreviewLogo("");

        setArchivoLogo(null);

        setEliminarLogoPendiente(
          Boolean(
            financiera?.logo
              ?.url ||
              financiera?.logo
                ?.publicId
          )
        );
      };

    /* ====================================================
       VALIDAR
    ==================================================== */

    const validar = () => {
      if (
        !clean(
          form.financiera
        )
      ) {
        return "Completá el nombre de la financiera.";
      }

      const email =
        clean(form.email);

      if (
        email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          email
        )
      ) {
        return "Ingresá un email válido.";
      }

      const fee =
        Number(
          form.feePorcentaje
        );

      if (
        !Number.isFinite(
          fee
        ) ||
        fee < 0 ||
        fee >= 100
      ) {
        return "El fee debe ser un valor entre 0 y 99,99.";
      }

      if (
        form.requiereCuotas ||
        form.tipoCalculo ===
          "POR_CUOTA"
      ) {
        if (
          !form.requiereCuotas
        ) {
          return "El cálculo por cuota requiere habilitar cuotas.";
        }

        if (
          !Array.isArray(
            form.reglasCuotas
          ) ||
          form.reglasCuotas
            .length === 0
        ) {
          return "Agregá al menos una regla de cuotas.";
        }

        const cuotasUsadas =
          new Set();

        for (
          const regla of
          form.reglasCuotas
        ) {
          const cuotas =
            Number(
              regla.cuotas
            );

          const feeCuota =
            Number(
              regla.feePorcentaje
            );

          if (
            !Number.isFinite(
              cuotas
            ) ||
            cuotas <= 0
          ) {
            return "Todas las reglas deben tener una cantidad válida de cuotas.";
          }

          if (
            cuotasUsadas.has(
              cuotas
            )
          ) {
            return `La cantidad ${cuotas} de cuotas está repetida.`;
          }

          cuotasUsadas.add(
            cuotas
          );

          if (
            !Number.isFinite(
              feeCuota
            ) ||
            feeCuota < 0 ||
            feeCuota >= 100
          ) {
            return "El fee de cada cuota debe estar entre 0 y 99,99.";
          }
        }
      }

      return "";
    };

    /* ====================================================
       PAYLOAD
    ==================================================== */

    const armarPayload =
      () => {
        const requiereCuotas =
          form.tipoCalculo ===
            "POR_CUOTA" ||
          Boolean(
            form.requiereCuotas
          );

        return {
          codfinanciera:
            form.codfinanciera ||
            undefined,

          financiera:
            clean(
              form.financiera
            ),

          razonsocial:
            clean(
              form.razonsocial
            ),

          cuit:
            clean(
              form.cuit
            ),

          domicilio:
            clean(
              form.domicilio
            ),

          telefono1:
            clean(
              form.telefono1
            ),

          email:
            clean(
              form.email
            ),

          contacto:
            clean(
              form.contacto
            ),

          observaciones:
            clean(
              form.observaciones
            ),

          activo:
            Boolean(
              form.activo
            ),

          feePorcentaje:
            Number(
              form.feePorcentaje ||
                0
            ),

          tipoCalculo:
            form.tipoCalculo,

          requiereCuotas,

          aplicaTax:
            Boolean(
              form.aplicaTax
            ),

          configuracionPagosActiva:
            Boolean(
              form.configuracionPagosActiva
            ),

          reglasCuotas:
            requiereCuotas
              ? form.reglasCuotas.map(
                  (
                    regla
                  ) => ({
                    cuotas:
                      Number(
                        regla.cuotas
                      ),

                    feePorcentaje:
                      Number(
                        regla.feePorcentaje ||
                          0
                      ),
                  })
                )
              : [],
        };
      };

    /* ====================================================
       GUARDAR
    ==================================================== */

    const guardar =
      async (e) => {
        e.preventDefault();

        setError("");

        const mensaje =
          validar();

        if (mensaje) {
          setError(
            mensaje
          );

          return;
        }

        try {
          setSaving(true);

          const payload =
            armarPayload();

          let resp;

          const idActual =
            idPersistido ||
            getId(
              financiera
            );

          if (idActual) {
            resp =
              await putFinancieraAdmin(
                idActual,
                payload
              );
          } else {
            resp =
              await postFinancieraAdmin(
                payload
              );
          }

          let guardada =
            obtenerFinancieraRespuesta(
              resp
            );

          const idGuardado =
            getId(
              guardada
            ) ||
            idActual;

          if (!idGuardado) {
            throw new Error(
              "La financiera se guardó, pero no se pudo obtener su ID."
            );
          }

          setIdPersistido(
            idGuardado
          );

          /*
            Si seleccionó nuevo logo,
            POST /logo reemplaza automáticamente
            al anterior.
          */
          if (
            archivoLogo
          ) {
            const respLogo =
              await subirLogoFinanciera(
                idGuardado,
                archivoLogo
              );

            guardada =
              obtenerFinancieraRespuesta(
                respLogo
              ) ||
              guardada;
          } else if (
            eliminarLogoPendiente
          ) {
            const respLogo =
              await eliminarLogoFinanciera(
                idGuardado
              );

            guardada =
              obtenerFinancieraRespuesta(
                respLogo
              ) ||
              guardada;
          }

          if (
            typeof onSaved ===
            "function"
          ) {
            await onSaved(
              guardada
            );
          }
        } catch (err) {
          console.error(
            "❌ Error guardando financiera:",
            err
          );

          setError(
            err?.response?.data
              ?.error ||
              err?.response?.data
                ?.message ||
              err?.message ||
              "No se pudo guardar la financiera."
          );
        } finally {
          setSaving(false);
        }
      };

    /* ====================================================
       RENDER
    ==================================================== */

    return (
      <Modal
        show={show}
        onHide={
          saving
            ? undefined
            : onHide
        }
        size="xl"
        centered
        scrollable
        backdrop="static"
        dialogClassName="fin-admin-modal"
      >
        <form
          onSubmit={
            guardar
          }
        >
          <Modal.Header
            closeButton={
              !saving
            }
          >
            <div>
              <Modal.Title>
                {esEdicion
                  ? "Editar financiera"
                  : "Nueva financiera"}
              </Modal.Title>

              <div className="fin-admin-modal-subtitle">
                Datos comerciales,
                reglas de financiación
                y disponibilidad.
              </div>
            </div>
          </Modal.Header>

          <Modal.Body>
            {error && (
              <div className="fin-admin-error">
                {error}
              </div>
            )}

            <div className="fin-admin-form-grid">
              {/* ======================================
                  INFORMACIÓN GENERAL
              ======================================= */}

              <section className="fin-admin-form-section fin-admin-form-main">
                <div className="fin-admin-section-title">
                  <span>
                    1
                  </span>

                  <div>
                    <strong>
                      Información
                      general
                    </strong>

                    <small>
                      Identificación de
                      la financiera.
                    </small>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-2">
                    <label className="form-label">
                      Código
                    </label>

                    <input
                      className="form-control"
                      value={
                        form.codfinanciera
                      }
                      disabled
                      placeholder="Auto"
                    />
                  </div>

                  <div className="col-md-5">
                    <label className="form-label">
                      Financiera *
                    </label>

                    <input
                      className="form-control"
                      name="financiera"
                      value={
                        form.financiera
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                      autoComplete="off"
                    />
                  </div>

                  <div className="col-md-5">
                    <label className="form-label">
                      Razón social
                    </label>

                    <input
                      className="form-control"
                      name="razonsocial"
                      value={
                        form.razonsocial
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      CUIT / Tax ID
                    </label>

                    <input
                      className="form-control"
                      name="cuit"
                      value={
                        form.cuit
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                    />
                  </div>

                  <div className="col-md-8">
                    <label className="form-label">
                      Domicilio
                    </label>

                    <input
                      className="form-control"
                      name="domicilio"
                      value={
                        form.domicilio
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Teléfono
                    </label>

                    <input
                      className="form-control"
                      name="telefono1"
                      value={
                        form.telefono1
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Email
                    </label>

                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={
                        form.email
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Contacto
                    </label>

                    <input
                      className="form-control"
                      name="contacto"
                      value={
                        form.contacto
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                    />
                  </div>
                </div>
              </section>

              {/* ======================================
                  LOGO
              ======================================= */}

              <section className="fin-admin-form-section fin-admin-form-logo">
                <div className="fin-admin-section-title">
                  <span>
                    2
                  </span>

                  <div>
                    <strong>
                      Logo
                    </strong>

                    <small>
                      JPG, PNG o WEBP.
                    </small>
                  </div>
                </div>

                <div className="fin-admin-logo-editor">
                  <div className="fin-admin-logo-preview">
                    {previewLogo ? (
                      <img
                        src={
                          previewLogo
                        }
                        alt="Logo financiera"
                      />
                    ) : (
                      <span>
                        Sin logo
                      </span>
                    )}
                  </div>

                  <label className="fin-admin-upload-btn">
                    Elegir imagen

                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      onChange={
                        seleccionarLogo
                      }
                      disabled={
                        saving
                      }
                    />
                  </label>

                  {previewLogo && (
                    <button
                      type="button"
                      className="fin-admin-remove-logo"
                      onClick={
                        marcarEliminarLogo
                      }
                      disabled={
                        saving
                      }
                    >
                      Quitar logo
                    </button>
                  )}

                  <small>
                    Máximo 2 MB.
                  </small>
                </div>
              </section>

              {/* ======================================
                  CONFIGURACIÓN FINANCIERA
              ======================================= */}

              <section className="fin-admin-form-section fin-admin-form-finance">
                <div className="fin-admin-section-title">
                  <span>
                    3
                  </span>

                  <div>
                    <strong>
                      Configuración
                      financiera
                    </strong>

                    <small>
                      Fee y forma de
                      cálculo.
                    </small>
                  </div>
                </div>

                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label">
                      Fee general %
                    </label>

                    <input
                      type="number"
                      min="0"
                      max="99.99"
                      step="0.01"
                      className="form-control"
                      name="feePorcentaje"
                      value={
                        form.feePorcentaje
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                    />
                  </div>

                  <div className="col-md-5">
                    <label className="form-label">
                      Tipo de cálculo
                    </label>

                    <select
                      className="form-select"
                      value={
                        form.tipoCalculo
                      }
                      onChange={
                        cambiarTipoCalculo
                      }
                      disabled={
                        saving
                      }
                    >
                      <option value="FONDEO_INVERSO">
                        Fondeo inverso
                      </option>

                      <option value="GROSS_UP">
                        Gross-up
                      </option>

                      <option value="MONTO_DIRECTO">
                        Monto directo
                      </option>

                      <option value="POR_CUOTA">
                        Por cuota
                      </option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <div className="fin-admin-toggle-card">
                      <div>
                        <strong>
                          Requiere cuotas
                        </strong>

                        <small>
                          Solicitar plan al
                          vendedor
                        </small>
                      </div>

                      <button
                        type="button"
                        className={`fin-admin-switch ${
                          form.requiereCuotas
                            ? "on"
                            : "off"
                        }`}
                        onClick={
                          cambiarRequiereCuotas
                        }
                        disabled={
                          saving
                        }
                      >
                        <span />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-md-6">
                    <div className="fin-admin-toggle-card">
                      <div>
                        <strong>
                          Aplica TAX
                        </strong>

                        <small>
                          {form.aplicaTax
                            ? "El importe financiado forma parte de la base del TAX."
                            : "Esta financiera no genera TAX."}
                        </small>
                      </div>

                      <button
                        type="button"
                        className={`fin-admin-switch ${
                          form.aplicaTax
                            ? "on"
                            : "off"
                        }`}
                        onClick={
                          cambiarAplicaTax
                        }
                        disabled={
                          saving
                        }
                      >
                        <span />
                      </button>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="fin-admin-toggle-card">
                      <div>
                        <strong>
                          Nueva configuración activa
                        </strong>

                        <small>
                          {form.configuracionPagosActiva
                            ? "Venta utilizará esta configuración."
                            : "Continúa usando la lógica histórica."}
                        </small>
                      </div>

                      <button
                        type="button"
                        className={`fin-admin-switch ${
                          form.configuracionPagosActiva
                            ? "on"
                            : "off"
                        }`}
                        onClick={
                          cambiarConfiguracionPagosActiva
                        }
                        disabled={
                          saving
                        }
                      >
                        <span />
                      </button>
                    </div>
                  </div>
                </div>

                {form.tipoCalculo ===
                  "FONDEO_INVERSO" && (
                  <div className="alert alert-info mt-3 mb-0">
                    <strong>
                      Fondeo inverso:
                    </strong>{" "}
                    el vendedor ingresará el monto
                    informado por la financiera.
                    El sistema descontará el fee y
                    obtendrá automáticamente el monto
                    aplicado a la venta.
                  </div>
                )}

                {/* ==================================
                    REGLAS DE CUOTAS
                =================================== */}

                {form.requiereCuotas && (
                  <div className="fin-admin-rules">
                    <div className="fin-admin-rules-header">
                      <div>
                        <strong>
                          Reglas de cuotas
                        </strong>

                        <small>
                          Configurá el fee
                          correspondiente a
                          cada plan.
                        </small>
                      </div>

                      <button
                        type="button"
                        onClick={
                          agregarCuota
                        }
                        disabled={
                          saving
                        }
                      >
                        + Agregar cuota
                      </button>
                    </div>

                    {form.reglasCuotas
                      .length === 0 ? (
                      <div className="fin-admin-rules-empty">
                        Todavía no hay
                        reglas cargadas.
                      </div>
                    ) : (
                      <div className="fin-admin-rules-list">
                        {form.reglasCuotas.map(
                          (
                            regla,
                            index
                          ) => (
                            <div
                              className="fin-admin-rule-row"
                              key={
                                index
                              }
                            >
                              <div>
                                <label>
                                  Cuotas
                                </label>

                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  className="form-control"
                                  value={
                                    regla.cuotas
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    cambiarRegla(
                                      index,
                                      "cuotas",
                                      e
                                        .target
                                        .value
                                    )
                                  }
                                  disabled={
                                    saving
                                  }
                                />
                              </div>

                              <div>
                                <label>
                                  Fee %
                                </label>

                                <input
                                  type="number"
                                  min="0"
                                  max="99.99"
                                  step="0.01"
                                  className="form-control"
                                  value={
                                    regla.feePorcentaje
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    cambiarRegla(
                                      index,
                                      "feePorcentaje",
                                      e
                                        .target
                                        .value
                                    )
                                  }
                                  disabled={
                                    saving
                                  }
                                />
                              </div>

                              <button
                                type="button"
                                className="fin-admin-rule-delete"
                                onClick={() =>
                                  eliminarRegla(
                                    index
                                  )
                                }
                                disabled={
                                  saving
                                }
                                title="Eliminar regla"
                              >
                                ×
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* ======================================
                  ESTADO / OBSERVACIONES
              ======================================= */}

              <section className="fin-admin-form-section fin-admin-form-bottom">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="fin-admin-toggle-card fin-admin-toggle-status">
                      <div>
                        <strong>
                          Estado
                        </strong>

                        <small>
                          {form.activo
                            ? "Disponible para operar"
                            : "No disponible para nuevas operaciones"}
                        </small>
                      </div>

                      <button
                        type="button"
                        className={`fin-admin-switch ${
                          form.activo
                            ? "on"
                            : "off"
                        }`}
                        onClick={
                          cambiarActivo
                        }
                        disabled={
                          saving
                        }
                      >
                        <span />
                      </button>
                    </div>
                  </div>

                  <div className="col-md-8">
                    <label className="form-label">
                      Observaciones
                    </label>

                    <textarea
                      className="form-control"
                      rows={3}
                      name="observaciones"
                      value={
                        form.observaciones
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        saving
                      }
                      placeholder="Notas internas u observaciones operativas..."
                    />
                  </div>
                </div>
              </section>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              type="button"
              onClick={
                onHide
              }
              disabled={
                saving
              }
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              variant="primary"
              disabled={
                saving
              }
            >
              {saving ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />

                  Guardando...
                </>
              ) : (
                "Guardar financiera"
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  };

export default ModalAdministrarFinanciera;